import { useMutation } from "react-query";
import { CheckCircle2, EyeOff, Lightbulb, Loader2, RotateCcw } from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { useToast } from "../../../../hooks/use-toast";
import { cn } from "../../../../utils/utils";
import { saasApi, saasErrorMessage } from "../shared/saasApi";
import { FindingStatus, RiskLevel, SaasFinding } from "../shared/saasTypes";
import { findingLabel, formatRelative, humanize } from "../shared/saasFormat";
import { MetaTag, RiskBadge } from "../shared/SaasUi";
import { WorkspaceContext } from "./workspaceContext";

const STRIP: Record<RiskLevel, string> = {
    CRITICAL: "bg-red-600",
    HIGH: "bg-orange-500",
    MEDIUM: "bg-amber-500",
    LOW: "bg-blue-500",
    INFO: "bg-slate-300",
};

const EVIDENCE_LABELS: Record<string, string> = {
    permission: "Permission",
    source: "Granted via",
    sourceName: "Source",
    observationDays: "Observed days",
    eventsInScope: "Events in scope",
    lastActivityAt: "Last activity",
    highestPermission: "Highest permission",
    grantCount: "Grants",
    visibility: "Visibility",
    defaultBranch: "Default branch",
    owners: "Owners",
    members: "Members",
    eventCount: "Events",
    threshold: "Threshold",
    category: "Category",
    lastIp: "Last IP",
    logSource: "Log source",
    state: "State",
};

const formatEvidence = (key: string, value: unknown) => {
    if (Array.isArray(value)) return `${value.length} items`;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (key.endsWith("At") && typeof value === "string") return formatRelative(value);
    if (typeof value === "string" && /^[A-Z_]+$/.test(value)) return humanize(value);
    return String(value);
};

export default function FindingCard({ finding, context, compact = false, onChanged }: {
    finding: SaasFinding;
    context: WorkspaceContext;
    compact?: boolean;
    onChanged?: () => void;
}) {
    const { toast } = useToast();
    const mutation = useMutation((status: FindingStatus) => saasApi.updateFinding(context.provider, context.tenantName, finding.id, status), {
        onSuccess: (updated) => {
            toast({ title: `Finding ${humanize(updated.status).toLowerCase()}` });
            onChanged?.();
        },
        onError: (error) => {
            toast({ title: "Unable to update the finding", description: saasErrorMessage(error, "Try again."), variant: "destructive" });
        },
    });

    const evidence = Object.entries(finding.evidence ?? {})
        .filter(([key, value]) => value !== null && value !== undefined && value !== "" && key in EVIDENCE_LABELS && !Array.isArray(value))
        .slice(0, 6);
    const listEvidence = Object.entries(finding.evidence ?? {}).find(([, value]) => Array.isArray(value)) as [string, string[]] | undefined;

    return (
        <Card className="cloud-list-card rounded-2xl relative overflow-hidden">
            <span className={cn("absolute left-0 inset-y-0 w-1", STRIP[finding.severity])} aria-hidden="true" />
            <CardContent className={cn(compact ? "p-3 pl-4" : "p-4 pl-5")}>
                <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <RiskBadge level={finding.severity} />
                            <MetaTag>{findingLabel(finding.findingType)}</MetaTag>
                            {finding.occurrences > 1 && <MetaTag>{finding.occurrences.toLocaleString()} occurrences</MetaTag>}
                            {finding.status !== "OPEN" && <MetaTag>{humanize(finding.status)}</MetaTag>}
                        </div>
                        <h3 className="font-semibold text-sm leading-snug">{finding.title}</h3>
                        {finding.description && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{finding.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {mutation.isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                        {finding.status === "OPEN" ? (
                            <>
                                <Button variant="outline" className="saas-small-button" disabled={mutation.isLoading} onClick={() => mutation.mutate("RESOLVED")}>
                                    <CheckCircle2 className="w-4 h-4" />
                                    Resolve
                                </Button>
                                <Button variant="outline" className="saas-small-button" disabled={mutation.isLoading} onClick={() => mutation.mutate("DISMISSED")}>
                                    <EyeOff className="w-4 h-4" />
                                    Dismiss
                                </Button>
                            </>
                        ) : (
                            <Button variant="outline" className="saas-small-button" disabled={mutation.isLoading} onClick={() => mutation.mutate("OPEN")}>
                                <RotateCcw className="w-4 h-4" />
                                Reopen
                            </Button>
                        )}
                    </div>
                </div>

                {!compact && finding.recommendation && (
                    <div className="mt-3 flex gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-[0_2px_8px_rgb(15_23_42/0.04)]">
                        <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span>{finding.recommendation}</span>
                    </div>
                )}

                <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                    {finding.identityLogin && (
                        <button
                            type="button"
                            className="text-primary font-medium hover:underline"
                            onClick={() => context.openIdentity(finding.connectionId, finding.identityLogin as string)}
                        >
                            {finding.identityLogin}
                        </button>
                    )}
                    {finding.resourceName && <span className="font-medium truncate max-w-xs" title={finding.resourceName}>{finding.resourceName}</span>}
                    {evidence.map(([key, value]) => (
                        <span key={key} className="text-muted-foreground">
                            {EVIDENCE_LABELS[key]}: <span className="text-foreground font-medium">{formatEvidence(key, value)}</span>
                        </span>
                    ))}
                    {listEvidence && listEvidence[1].length > 0 && (
                        <span className="text-muted-foreground truncate max-w-md" title={listEvidence[1].join(", ")}>
                            {listEvidence[1].slice(0, 3).join(", ")}
                            {listEvidence[1].length > 3 ? ` +${listEvidence[1].length - 3} more` : ""}
                        </span>
                    )}
                    <span className="ml-auto text-muted-foreground">Last detected {formatRelative(finding.lastDetectedAt)}</span>
                </div>
            </CardContent>
        </Card>
    );
}
